// @file Pinia store for managing recent emojis
import { __ } from '@@/bits/intl'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import type { Emoji as EmojiType } from '@padlet/arvo'
import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed } from 'vue'

const STORAGE_KEY_PREFIX = 'recent-emojis'
const MAX_COUNT = 16

const RECENT_EMOJIS_CATEGORY = { label: 'recents', title: __('Recents') } as const

interface RecentEmojiEntry {
  emoji: EmojiType
  usageCount: number
  lastUsedAt: number
}

/**
 * Ensures that the most recently used emoji is visible in the top `maxCount` entries.
 * If the just used emoji is provided, it will be prioritized to be visible.
 * If the list exceeds `maxCount`, it will swap the most recent one with the last visible position.
 */
function ensureRecentlyUsedVisible(
  entries: RecentEmojiEntry[],
  maxCount: number,
  justUsedEmojiUrl?: string,
): RecentEmojiEntry[] {
  // Handle edge cases for maxCount
  if (maxCount <= 0) {
    return []
  }

  if (entries.length <= maxCount) {
    return entries
  }

  // Find the emoji we need to keep visible
  const targetEmojiIndex = justUsedEmojiUrl
    ? entries.findIndex((entry) => entry.emoji.url === justUsedEmojiUrl)
    : entries.reduce((maxIdx, entry, idx, arr) => (entry.lastUsedAt > arr[maxIdx].lastUsedAt ? idx : maxIdx), 0)

  if (targetEmojiIndex === -1 || targetEmojiIndex < maxCount) {
    // Already visible, just slice
    return entries.slice(0, maxCount)
  }

  // Swap with last visible position
  const result = [...entries]
  const temp = result[maxCount - 1]
  result[maxCount - 1] = result[targetEmojiIndex]
  result[targetEmojiIndex] = temp

  return result.slice(0, maxCount)
}

const useRecentEmojisStore = defineStore('recentEmojis', () => {
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()
  const STORAGE_KEY = surfaceCurrentUserStore.currentUser?.hashid
    ? `${STORAGE_KEY_PREFIX}:${surfaceCurrentUserStore.currentUser.hashid}`
    : STORAGE_KEY_PREFIX

  const recentEmojiEntries = useLocalStorage<RecentEmojiEntry[]>(STORAGE_KEY, [], {
    serializer: {
      read: (value: string) => {
        try {
          const parsed = JSON.parse(value)
          if (!Array.isArray(parsed)) {
            return []
          }

          // Check if data matches current RecentEmojiEntry[] format
          const isValidFormat = parsed.every(
            (item) =>
              item != null &&
              typeof item === 'object' &&
              'emoji' in item &&
              'usageCount' in item &&
              'lastUsedAt' in item &&
              typeof item.usageCount === 'number' &&
              typeof item.lastUsedAt === 'number',
          )

          if (!isValidFormat) {
            return [] // Nuke any data that doesn't match current format
          }

          return parsed
        } catch {
          return []
        }
      },
      write: (value: RecentEmojiEntry[]) => asciiSafeStringify(value),
    },
  })

  function addToRecents(emoji: EmojiType): void {
    const now = Date.now()
    const currentEntries = [...recentEmojiEntries.value]
    const existingIndex = currentEntries.findIndex((entry) => entry.emoji.url === emoji.url)

    if (existingIndex !== -1) {
      // Update existing emoji with incremented usage count
      currentEntries[existingIndex] = {
        emoji: { ...emoji, category: RECENT_EMOJIS_CATEGORY },
        usageCount: currentEntries[existingIndex].usageCount + 1,
        lastUsedAt: now,
      }
    } else {
      // Add new emoji with initial count of 1
      currentEntries.push({
        emoji: { ...emoji, category: RECENT_EMOJIS_CATEGORY },
        usageCount: 1,
        lastUsedAt: now,
      })
    }

    // Sort by usage count (descending), then by lastUsedAt (descending) when counts are equal
    currentEntries.sort((a, b) => {
      if (b.usageCount !== a.usageCount) {
        return b.usageCount - a.usageCount
      }
      return b.lastUsedAt - a.lastUsedAt
    })

    // Use helper to ensure visibility when trimming
    recentEmojiEntries.value = ensureRecentlyUsedVisible(currentEntries, MAX_COUNT, emoji.url)
  }

  function clearRecents(): void {
    recentEmojiEntries.value = []
  }

  const recentEmojis = computed(() => {
    // Since we already sort in addToRecents, just extract the emojis
    return recentEmojiEntries.value.map((entry) => entry.emoji)
  })

  function getRecentEmojisSubset(count: number): EmojiType[] {
    const visibleEntries = ensureRecentlyUsedVisible(recentEmojiEntries.value, count)
    return visibleEntries.map((entry) => entry.emoji)
  }

  return {
    recentEmojis,
    addToRecents,
    clearRecents,
    getRecentEmojisSubset,
  }
})

/**
 * Clear recent emojis from localStorage for the current user.
 */
function clearRecentEmojisFromStorage(): void {
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()
  const STORAGE_KEY = surfaceCurrentUserStore.currentUser?.hashid
    ? `${STORAGE_KEY_PREFIX}:${surfaceCurrentUserStore.currentUser.hashid}`
    : STORAGE_KEY_PREFIX
  safeLocalStorage.removeItem(STORAGE_KEY)
}

export { RECENT_EMOJIS_CATEGORY, useRecentEmojisStore, clearRecentEmojisFromStorage }
